import React, { useCallback, useEffect, useMemo } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useOverlayTriggerState } from 'react-stately';

import { Popover } from '@/lib/base/overlays/popover';

import type { ImageSize } from './ImageElement';

interface ImageSizeMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSizeChange: (size: ImageSize) => void;
  onDelete: () => void;
  anchorElement: HTMLElement | null;
  currentSize?: ImageSize;
}

const SIZE_OPTIONS: { label: string; value: ImageSize; description: string }[] =
  [
    { label: 'S', value: 'small', description: 'Small (200px)' },
    { label: 'M', value: 'medium', description: 'Medium (400px)' },
    { label: 'L', value: 'large', description: 'Large (100%)' },
  ];

/** Gap between the (visible part of the) image and the menu. */
const MENU_GAP = 8;

/**
 * Walk up the DOM looking for an ancestor that scrolls (overflow auto/scroll
 * on either axis with content actually overflowing). Used to find the email
 * editor's scrollable body so we can clip the image rect to its visible
 * region — without this, an image taller than the editor's max-height makes
 * `getBoundingClientRect()` return bounds that extend past the visible scroll
 * viewport, dropping the menu far below the on-screen image.
 */
function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node);
    const scrollsY =
      (cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight;
    const scrollsX =
      (cs.overflowX === 'auto' || cs.overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth;
    if (scrollsY || scrollsX) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * react-aria positions the popover against the anchor's FULL rect. When the
 * image overflows the editor's scroll viewport, shift the menu back onto the
 * visible portion: `offset` pulls it up to the visible bottom edge and
 * `crossOffset` re-centres it on the visible horizontal span.
 */
function getVisibleAnchorOffsets(anchor: HTMLElement): {
  offset: number;
  crossOffset: number;
} {
  const rect = anchor.getBoundingClientRect();
  const scroller = getScrollableAncestor(anchor);
  if (!scroller) {
    return { offset: MENU_GAP, crossOffset: 0 };
  }
  const sr = scroller.getBoundingClientRect();
  const visibleBottom = Math.min(rect.bottom, sr.bottom);
  const visibleLeft = Math.max(rect.left, sr.left);
  const visibleRight = Math.min(rect.right, sr.right);
  return {
    offset: MENU_GAP - (rect.bottom - visibleBottom),
    crossOffset:
      (visibleLeft + visibleRight) / 2 - (rect.left + rect.right) / 2,
  };
}

interface SizeOptionButtonProps {
  label: string;
  value: ImageSize;
  description: string;
  isCurrent: boolean;
  onSelect: (size: ImageSize) => void;
}

function SizeOptionButton({
  label,
  value,
  description,
  isCurrent,
  onSelect,
}: SizeOptionButtonProps) {
  const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
  return (
    <button
      type='button'
      onClick={handleClick}
      className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition duration-100 ease-linear ${
        isCurrent
          ? 'bg-utility-brand-50 text-utility-brand-700'
          : 'bg-secondary text-secondary hover:bg-secondary_hover'
      }`}
      title={description}
      role='menuitem'
    >
      {label}
    </button>
  );
}

/**
 * Size/delete menu for an inline editor image, anchored to the image element.
 *
 * Built on the lib/base Popover (non-modal): it portals with react-aria's
 * top-layer handling (so it stays interactive inside a react-aria Modal),
 * stacks via StackingContext, dismisses on outside pointer interaction via
 * useDismissOnInteractOutside, closes when an ancestor of the image scrolls
 * (react-aria useCloseOnScroll) and repositions on resize. The popover reads
 * its open state from OverlayTriggerStateContext, which we supply from the
 * controlled `isOpen`/`onClose` props since there is no react-aria trigger.
 */
export const ImageSizeMenu: React.FC<ImageSizeMenuProps> = ({
  isOpen,
  onClose,
  onSizeChange,
  onDelete,
  anchorElement,
  currentSize,
}) => {
  const isMenuOpen = isOpen && anchorElement !== null;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );
  const state = useOverlayTriggerState({
    isOpen: isMenuOpen,
    onOpenChange: handleOpenChange,
  });

  const anchorRef = useMemo(
    () => ({ current: anchorElement }),
    [anchorElement]
  );

  // Measured when the menu opens (or re-anchors to another image). Scrolling
  // closes the menu, so the measurement cannot go stale while it is open.
  const anchorOffsets = useMemo(
    () =>
      isMenuOpen && anchorElement
        ? getVisibleAnchorOffsets(anchorElement)
        : { offset: MENU_GAP, crossOffset: 0 },
    [isMenuOpen, anchorElement]
  );

  // Focus stays on the image (the editor keeps its selection and resize
  // handles), so react-aria's popover-scoped Escape handler never sees the
  // key. Listen at the document while open, as the previous menu did.
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen, onClose]);

  const handleSizeClick = useCallback(
    (size: ImageSize) => {
      onSizeChange(size);
      onClose();
    },
    [onSizeChange, onClose]
  );

  const handleDeleteClick = useCallback(() => {
    onDelete();
    onClose();
  }, [onDelete, onClose]);

  // Normalize current size for comparison
  const normalizedCurrentSize =
    typeof currentSize === 'number' ? undefined : currentSize;

  return (
    <OverlayTriggerStateContext value={state}>
      <Popover
        size='xs'
        triggerRef={anchorRef}
        placement='bottom'
        offset={anchorOffsets.offset}
        crossOffset={anchorOffsets.crossOffset}
        containerPadding={MENU_GAP}
        className='min-w-[160px] rounded-lg bg-primary p-2 shadow-lg ring-0 border border-secondary'
      >
        <div role='menu' aria-label='Image size options'>
          {/* Size presets */}
          <div className='flex items-center gap-1 mb-2 pb-2 border-b border-secondary'>
            {SIZE_OPTIONS.map(option => (
              <SizeOptionButton
                key={option.value}
                label={option.label}
                value={option.value}
                description={option.description}
                isCurrent={normalizedCurrentSize === option.value}
                onSelect={handleSizeClick}
              />
            ))}
          </div>

          {/* Delete button */}
          <button
            type='button'
            onClick={handleDeleteClick}
            className='w-full flex items-center gap-2 px-3 py-2 text-sm text-error-primary hover:bg-error-primary rounded transition duration-100 ease-linear'
            role='menuitem'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
              />
            </svg>
            <span>Delete image</span>
          </button>
        </div>
      </Popover>
    </OverlayTriggerStateContext>
  );
};

export default ImageSizeMenu;
